import { useMemo } from 'react';
import { protoEventToEventGroup, SearchGroup } from '@fullstory/search-model';
import { Flags } from '@fullstory/client-api';
import { UID, WithID } from '@fullstory/util/uid';
import { EventType, NONE } from '@fullstory/search-types';
import { FunnelRecord } from '@fullstory/proto/fs/services/search-settings';
import {
  Behavior,
  Event,
  EventSequence,
  Platform,
  TimeInterval,
} from '@fullstory/proto/fs/api/segment';
import { Card } from '@fullstory/proto/fs/services/dx-objects';
import {
  AnalysisMode,
  ComputeFunnelMetricsRequest,
  ComputeFunnelMetricsResponse,
  QueryCacheOptions,
} from '@fullstory/proto/fs/services/alexa';
import { Duration, int64 } from '@fullstory/proto/google/protobuf';
import { useSelector } from 'react-redux';
import { AppState } from '../../types';
import jstz from 'jstz';
import { FunnelMetricsRequestBody, FunnelsById } from './types';
import { Computation as FunnelComputation } from '@fullstory/proto/fs/api/metrics/funnelmetrics';
import {
  Computation as MetricComputation,
  Measure,
  Metric,
  NumericAggregationMethod,
  UniqueAggregation,
} from '@fullstory/proto/fs/api/metrics';
import { EventSearch, Scope } from '@fullstory/proto/fs/api/search';
import { RevenueEvent } from '@fullstory/proto/fs/services/webber/conversions';
import { intl } from '@fullstory/intl';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { getObjectWithOverlayStorageId } from '../../utils';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';

export const getEventTextByStepIndex = (
  events: EventSequence,
  index: number,
  fallback: string,
  flags: Flags,
  displayFn: (e: WithID<EventType>) => string | undefined,
): string => {
  let eventText: string | null | undefined = null;
  const event = events?.events[index];
  if (event) {
    const searchGroup = eventStateToEventType(event, flags);
    if (searchGroup) {
      eventText = displayFn(searchGroup.primary);
    }
  }
  return eventText ? eventText : fallback;
};

export const eventStateToEventType = (
  event: Event | null,
  flags: Flags,
  disabled?: boolean,
): SearchGroup<EventType> => {
  if (!event || (!event.primary && event.deps?.length === 0)) {
    return {
      primary: {
        kind: 'any',
        subkind: NONE,
        value: NONE,
        id: UID(),
      },
      dependents: [],
      disabled,
    };
  }
  return protoEventToEventGroup(event, flags);
};

export const getConversionPctFromFunnelMetrics = (
  funnelData: ComputeFunnelMetricsResponse | null,
): number => {
  if (!funnelData || !funnelData?.data?.steps) return 0;
  const counts = funnelData.data.steps;
  if (!counts.length || counts.length === 1 || !counts[0]) return 0;
  if (!counts[0].table[0] || counts[0].table[0]?.value === 0) return 0;
  return (counts[counts.length - 1]?.table[0]?.value ?? 0) / counts[0].table[0].value;
};

export const analysisModeToInSameSession = (analysisMode: AnalysisMode): boolean =>
  analysisMode === AnalysisMode.ANALYSIS_MODE_SESSIONS ||
  analysisMode === AnalysisMode.ANALYSIS_MODE_INDV_SAME_SESSION;

export const analysisModeToIsSessionMode = (analysisMode: AnalysisMode): boolean =>
  analysisMode === AnalysisMode.ANALYSIS_MODE_SESSIONS;

// TODO - For now, just handle inSameSessionNess, we will need to address Session Scope as a future enhancement
export const analysisModeForFunnel = (funnelEvents: EventSequence | null): AnalysisMode => {
  if (!funnelEvents || !funnelEvents.constraints) return AnalysisMode.ANALYSIS_MODE_INVALID;
  return funnelEvents.constraints.inSameSession
    ? AnalysisMode.ANALYSIS_MODE_INDV_SAME_SESSION
    : AnalysisMode.ANALYSIS_MODE_INDV_ACROSS_SESSIONS;
};

/**
 * Primarily broken out for use with the hook below, but some use cases cannot utilize that hook.
 * Arguments broken out to keep the memoization dependencies intact with the hook below (avoids runaway effects).
 */
export const getSettingsFunnel = (
  card: Card,
  funnelsById: FunnelsById,
  dashboardOverlayHash?: string,
): FunnelRecord => {
  if (card.funnelId) {
    const funnelDetail =
      funnelsById[getObjectWithOverlayStorageId(card.funnelId, dashboardOverlayHash)];
    if (funnelDetail?.funnel?.funnel) {
      return funnelDetail.funnel;
    }
  }
  const funnelName =
    card.name !== ''
      ? card.name
      : intl.formatMessage({ defaultMessage: 'Custom Funnel', id: 'A7YvOa' });
  const funnelDescription = card.description !== '' ? card.description : '';
  const metadata = card?.metadata
    ? {
        lastUpdated: card.metadata.updatedAt,
        lastUpdatedBy: card.metadata.updatedBy,
        created: card.metadata.createdAt,
        createdBy: card.metadata.createdBy,
        archived: false,
        version: int64.fromNumber(1),
        sequentialVersion: int64.fromNumber(1),
        visibility: Visibility.UNSET,
      }
    : null;

  if (card.conversionFunnel?.funnelId) {
    const funnelDetail =
      funnelsById[
        getObjectWithOverlayStorageId(card.conversionFunnel.funnelId, dashboardOverlayHash)
      ];

    if (funnelDetail?.funnel?.funnel) {
      return {
        ...funnelDetail.funnel,
        funnel: {
          ...funnelDetail.funnel.funnel,
          name: card.name === '' ? funnelDetail.funnel.funnel.name : funnelName,
          description:
            card.description === '' ? funnelDetail.funnel.funnel.description : funnelDescription,
          funnelSettings: card.conversionFunnel,
        },
        metadata,
      };
    }
  }
  return {
    funnel: {
      id: '',
      name: funnelName,
      description: funnelDescription,
      events: card.conversionFunnel?.events ?? null,
      funnelSettings: card.conversionFunnel ?? null,
      aggregation: UniqueAggregation.UNIQUE_AGGREGATION_INDIVIDUAL,
    },
    metadata,
    isDcmCreated: false,
  };
};

/**
 * Hook to provide either the stored funnel from the conversionFunnel's funnelId,
 * or a funnel from the conversionFunnel's embedded events.
 */
export const useSettingsFunnel = (card: Card): FunnelRecord => {
  const funnelsById = useSelector((state: AppState) => state.funnels.funnels);

  return useMemo(() => getSettingsFunnel(card, funnelsById), [card, funnelsById]);
};

export const funnelMetricsRequestBodyToComputeFunnelMetricsRequest = (
  body: FunnelMetricsRequestBody,
): ComputeFunnelMetricsRequest => ({
  orgId: body.orgId,
  computation: body.computation,
  now: null, // current time is implied
  timezone: jstz.determine().name(),
  cacheOptions: body.cacheOptions ?? null,
  sampleOptions: body.sampleOptions ?? null,
  requestAnalytics: body.requestAnalytics ?? null,
});

const second = 1000;
const minute = 60 * second;
const hour = 60 * minute;

export const funnelsCacheOptions: QueryCacheOptions = {
  getIfAgeLessThan: Duration.fromMillis(2 * hour),
  computeIfOlderThan: Duration.fromMillis(hour),
  skipCache: false,
  queue: QueryCacheOptions.Queue.QUEUE_INVALID,
};

// funnelsCacheOptionsRefresh is query cache options to refresh data
// for metrics. The values match the most aggressive values that
// the server allows.
export const funnelsCacheOptionsRefresh: QueryCacheOptions = {
  getIfAgeLessThan: Duration.fromMillis(5 * minute),
  computeIfOlderThan: Duration.fromMillis(minute),
  skipCache: false,
  queue: QueryCacheOptions.Queue.QUEUE_INVALID,
};

// funnelsCacheOptionsRefreshMinute is query cache options to refresh data
// for metrics. The values match the most aggressive values that
// the server allows for minute trends.
export const funnelsCacheOptionsRefreshMinute: QueryCacheOptions = {
  getIfAgeLessThan: Duration.fromMillis(minute),
  computeIfOlderThan: Duration.fromMillis(minute),
  skipCache: false,
  queue: QueryCacheOptions.Queue.QUEUE_INVALID,
};

export const buildRevOppsMetricsRequest = (
  funnelComputation: FunnelComputation | null,
  revenueEvent: RevenueEvent | null,
  aggregation: NumericAggregationMethod,
): MetricComputation => {
  const primary = revenueEvent
    ? revenueEvent.definedEventId
      ? { definedEvent: { withId: { value: revenueEvent.definedEventId } } }
      : { custom: { named: { name: revenueEvent.eventName! } } }
    : null;
  const metric: Metric = {
    single: {
      event: {
        primary,
        deps: [],
      },
      aggregation: {
        numeric: {
          property: {
            customEventProperty: revenueEvent?.eventProperty,
          },
          aggregation,
          percentileConfig: null,
        },
      },
      rollingWindow: 0,
      rollingWindowType: Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID,
      rollingWindowInterval: TimeInterval.TIME_INTERVAL_INVALID,
    },
  };

  const funnelScope: Scope = funnelComputation?.funnel?.events?.constraints?.inSameSession
    ? Scope.SCOPE_SESSION
    : Scope.SCOPE_INDIVIDUAL;

  const funnelMatchingBehaviors: EventSearch.MatchingBehaviors = {
    excludeBehaviors: [],
    behaviors: (funnelComputation?.funnel?.events?.events || []).map(
      (event): Behavior => ({ event }),
    ),
    constraints: funnelComputation?.funnel?.events?.constraints || {
      inOrder: true,
      inSameSession: true,
      platform: Platform.PLATFORM_INVALID,
      timeRange: funnelComputation?.timeRange || null,
      completeSessions: false,
      withinTime: Duration.zero,
      eventThresholds: [],
    },
    excludeConstraints: null,
    widenToScope: funnelScope,
  };

  const matchingBehaviors: EventSearch.MatchingBehaviors[] = [
    ...(funnelComputation?.matchingBehaviors ?? []),
    funnelMatchingBehaviors,
  ];

  return {
    metric,
    timeRange: funnelComputation?.timeRange || null,
    timeIntervalRange: null,
    timeInterval: TimeInterval.TIME_INTERVAL_ENTIRE_RANGE,
    segmentDefinitions: funnelComputation ? [...funnelComputation.segmentDefinitions] : [],
    matchingBehaviors,
    dimensionSettings: null,
  };
};

const getLastEventDefinedEventId = (funnelEvents: EventSequence | null | undefined) => {
  if (funnelEvents) {
    const events = funnelEvents.events;
    if (events) {
      const lastEvent = events[events.length - 1];
      return lastEvent?.primary?.definedEvent?.withId?.value;
    }
  }

  return undefined;
};

export const isRevenueFunnel = (
  funnelEvents: EventSequence | null | undefined,
  revenueEvent: RevenueEvent | null,
) => {
  if (revenueEvent?.disabled || !funnelEvents) return false;

  if (revenueEvent) {
    return getLastEventDefinedEventId(funnelEvents) === revenueEvent?.definedEventId;
  }
  return false;
};

export const isMaybeRevenueFunnel = (
  funnelEvents: EventSequence | null | undefined,
  flags: Flags,
) => flags.hasBit(Flag.FeatImpacts) && !!getLastEventDefinedEventId(funnelEvents);
